import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface AssetCategory {
  id?: number;
  name: string;
  description: string;
  depreciationRate: number;
  usefulLife: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AssetCategoriesService {
  private apiUrl = `${environment.apiServiceBaseUrl}/hawdco/assets/categories`;

  constructor(private http: HttpClient) {}

  // Get all asset categories with optional pagination and filtering
  getAssetCategories(page: number = 1, limit: number = 10, search?: string): Observable<ApiResponse<AssetCategory[]>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    
    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<AssetCategory[]>(this.apiUrl, { params })
      .pipe(
        map(data => ({
          success: true,
          data: data,
          message: 'Asset categories loaded successfully',
          total: data.length
        })),
        catchError(this.handleError)
      );
  }

  // Get asset category by ID
  getAssetCategoryById(id: number): Observable<ApiResponse<AssetCategory>> {
    return this.http.get<ApiResponse<AssetCategory>>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Create new asset category
  createAssetCategory(category: AssetCategory): Observable<ApiResponse<AssetCategory>> {
    return this.http.post<ApiResponse<AssetCategory>>(this.apiUrl, category)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Update asset category
  updateAssetCategory(id: number, category: AssetCategory): Observable<ApiResponse<AssetCategory>> {
    return this.http.put<ApiResponse<AssetCategory>>(`${this.apiUrl}/${id}`, category)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Delete asset category
  deleteAssetCategory(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Get asset categories statistics
  getAssetCategoriesStats(): Observable<ApiResponse<{
    totalCategories: number;
    totalAssets: number;
    totalValue: number;
    averageDepreciationRate: number;
  }>> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/stats`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // Export asset categories to Excel
  exportToExcel(search?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }

    return this.http.get(`${this.apiUrl}/export/excel`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Export asset categories to PDF
  exportToPDF(search?: string): Observable<Blob> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }

    return this.http.get(`${this.apiUrl}/export/pdf`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(this.handleError)
    );
  }

  // Error handling
  private handleError(error: any): Observable<never> {
    let errorMessage = 'An error occurred';
    
    if (error.error instanceof ErrorEvent) {
      // Client-side error
      errorMessage = `Error: ${error.error.message}`;
    } else {
      // Server-side error
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
      
      if (error.error && error.error.message) {
        errorMessage = error.error.message;
      }
    }

    console.error('API Error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
